import type { IFieldDefinition, IFormDefinition, IFormVariants, IRecordTypeDefinition } from '@platform/core/types';
import { resolveGroups } from './formLayout';

/** Варіант форми типу запису: rt.forms[варіант], інакше повна форма (`form`). */
export function formVariantOf(rt: IRecordTypeDefinition, variant: keyof IFormVariants): IFormDefinition {
  return rt.forms?.[variant] ?? rt.form;
}

/**
 * Поля контекстної форми (права панель Split): `forms.context.fields` → ключовий рядок і групи `forms.context` →
 * fallback повної форми (ключовий рядок + поля її груп за визначенням, до 10 полів). Без `title` — його показує заголовок панелі.
 */
export function contextFieldsOf(rt: IRecordTypeDefinition): IFieldDefinition[] {
  const context = rt.forms?.context;
  const configured = context?.fields?.length
    ? context.fields
    : context && (context.keyLine.length || context.groups.length)
      ? [...context.keyLine, ...context.groups.flatMap((g) => g.fields)]
      : undefined;
  const keyLine = rt.form.keyLine;
  const keys = configured ?? [...keyLine, ...resolveGroups(rt).flatMap((g) => g.fields).filter((k) => !keyLine.includes(k))].slice(0, 10);
  return fieldsOf(rt, keys.filter((k) => k !== 'title'));
}

/**
 * Поля міні-форми (UIUX forms/form-variants, прототип v4 — швидка форма): `forms.mini.fields` → ключовий рядок і групи `forms.mini` →
 * fallback «Статус · Пріоритет · Термін · Виконавець · Опис» (лише ті, що є в типі). Без `title` — його редагує шапка міні-форми.
 */
export function miniFieldsOf(rt: IRecordTypeDefinition): IFieldDefinition[] {
  const mini = rt.forms?.mini;
  const configured = mini?.fields?.length
    ? mini.fields
    : mini && (mini.keyLine.length || mini.groups.length)
      ? [...mini.keyLine, ...mini.groups.flatMap((g) => g.fields)]
      : ['status', 'priority', 'due', 'assignee', 'description'];
  return fieldsOf(rt, configured.filter((k) => k !== 'title'));
}

/** Колонки списку вибору посилання (RecordLookup): `forms.lookup.fields` без title (код і назва показуються завжди). */
export function lookupFieldsOf(rt: IRecordTypeDefinition): IFieldDefinition[] {
  return fieldsOf(rt, (rt.forms?.lookup?.fields ?? []).filter((k) => k !== 'title'));
}

/** Визначення полів за ключами без дублікатів і невідомих полів, у заданому порядку. */
function fieldsOf(rt: IRecordTypeDefinition, keys: string[]): IFieldDefinition[] {
  const seen = new Set<string>();
  return keys.filter((k) => !seen.has(k) && seen.add(k)).map((k) => rt.fields.find((f) => f.key === k)).filter((f): f is IFieldDefinition => Boolean(f));
}
